import type { LegacyAction, LegacyActionOption } from 'flow/store/engine/types/legacy';
import apiCalls from '~/helpers/apiCalls';
import log from '~/helpers/logging';
import type {
  CustomListOverview, CustomList, CustomListColumn,
} from './types';
import type {
  Action,
  ActionOption,
  Attribute, AttributeComponent,
} from 'flow/store/engine/types';
import { CUSTOM_LIST_SERVICE_KEY_IN_SERVICES } from 'flow/service/services/constants';
import { isActionOfType } from '../../actions';
import { ActionType } from 'flow/store/engine/constants';


// ======== Process Custom Lists into a format that can be used in the workflow editor =================

const findOption = (
  actionOptions: LegacyActionOption[], action: LegacyAction, key: string,
) => actionOptions.find(option => option.actionId === action.id && option.key === key);

/**
 * Finds all custom list service actions in a workflow and fetches their versions
 * @param typhonDomain - The typhon domain for API calls
 * @param workflowData - The workflow data containing actions
 * @returns an array of all active custom lists with versions array
 * that contains the active version and any other major versions used in the workflow
 */
export async function getCustomListsWithVersions(
  args: {
    typhonDomain: string;
    workflowData: { actions: LegacyAction[]; actionOptions: LegacyActionOption[] };
    customLists: CustomListOverview[];
  },
): Promise<CustomList[]> {
  const { typhonDomain, workflowData, customLists: allCustomLists } = args;
  const { actions, actionOptions } = workflowData;

  const customListActions = actions.filter(
    action => action.type === 'run' && action.name === 'Custom List',
  );

  // Find all the custom lists and their versions used in the workflow:
  // { token: Set<majorVersion1, majorVersion2, ...> }
  const versionsByToken: Record<string, Set<string>> = customListActions.reduce((acc, action) => {
    const tokenOption = findOption(actionOptions, action, 'custom_list_token') || {} as LegacyActionOption;
    const versionOption = findOption(actionOptions, action, 'custom_list_major_version') || {} as LegacyActionOption;

    if (tokenOption.value && versionOption.value) {
      const token = tokenOption.value.toString();
      if (!acc[token]) {
        acc[token] = new Set<string>();
      }
      acc[token].add(versionOption.value.toString());
    }
    return acc;
  }, {});

  // Fetch each custom list which provides all the versions
  const tokens = Object.keys(versionsByToken);
  const selectedCustomLists = await Promise.all(tokens.map(async token => {
    try {
      const customList = await apiCalls.customLists.getVersions({
        typhonDomain,
        customListToken: token,
      });
      return customList;
    } catch (error) {
      log(`Failed to fetch versions for custom list ${token}:`, error);
      return {};
    }
  }));

  const allActiveCustomLists = allCustomLists.filter(list => list.active_version);

  // Enhance selected custom lists with [versions] that contains all latest major version used
  const updatedCustomLists: CustomList[] = [];

  for (const customList of allActiveCustomLists) {
    const selectedCustomList = selectedCustomLists.find(
      list => list && list.custom_list_token === customList.custom_list_token,
    );
    const versions: CustomList['versions'] = [];
    const activeMajorVersion = Number(customList.active_version.split('.')[0]);
    if (selectedCustomList) {
      // First need to find the active major version of the custom list and push into versions array
      const activeVersion = selectedCustomList.versions.find(
        version => version.major_version === Number(activeMajorVersion),
      );
      if (activeVersion) {
        versions.push(activeVersion);
      }

      // Now find all the other major versions used in the workflow and push into versions array
      const usedMajorVersions = versionsByToken[selectedCustomList.custom_list_token];
      for (const majorVersion of usedMajorVersions) {
        // Since we already added the active version, we don't need to add it again
        if (Number(majorVersion) !== activeMajorVersion) {
          const version = selectedCustomList.versions.find(
            v => v.major_version === Number(majorVersion),
          );
          if (version) {
            versions.push(version);
          }
        }
      }
    }

    updatedCustomLists.push({
      ...customList,
      active_major_version: Number(activeMajorVersion),
      versions,
    });
  }

  return updatedCustomLists;
}

// ======== Helper Functions for Custom Lists =================

export function isCustomListAction(action: Action): boolean {
  return isActionOfType(action, ActionType.SERVICE)
    && action.service.internalName === CUSTOM_LIST_SERVICE_KEY_IN_SERVICES;
}

export function isUsingActiveListVersion(action: Action, customLists: CustomList[]): boolean {
  const customListToken = action.options?.custom_list_token?.value;
  const currentMajorVersion = action.options?.custom_list_major_version?.value;
  if (!customListToken || !currentMajorVersion) return false;

  const customList = customLists.find(list => list.custom_list_token === customListToken);
  if (!customList) return false;

  return Number(currentMajorVersion) === customList.active_major_version;
}


// ======== Helper Functions for Custom Lists in MLAs =================

export const CUSTOM_LIST_ATTRIBUTE_NAME = 'custom_list_attribute';
export const CUSTOM_LIST_COLUMNS_PLACEHOLDER = '{custom_list_columns}';
export const CUSTOM_LIST_SCORE_PLACEHOLDER = '{custom_list_match_score}';

// Prevent metadata columns from being show as we do not offer matching on these fields:
export const CUSTOM_LIST_METADATA_COLUMNS = new Set(['list_row_id', 'expires_at']);

export function filterOutMetadataColumns(columns: CustomListColumn[]): CustomListColumn[] {
  return columns.filter(column => !CUSTOM_LIST_METADATA_COLUMNS.has(column.name));
}

export function isCustomListAttribute(attribute: Attribute): boolean {
  return attribute.attribute_name === CUSTOM_LIST_ATTRIBUTE_NAME;
}

export function isMultipleOrAllColumnsSelected(
  components: AttributeComponent[],
): boolean {
  let columnsComponent = null;
  columnsComponent = components.find(
    component => component.attributeComponent.placeholder === CUSTOM_LIST_COLUMNS_PLACEHOLDER,
  );
  if (!columnsComponent) return false;

  const columns = columnsComponent.value;
  return columns.length > 1 || columns[0] === 'all';
}

const MatchScoreLabels = {
  AVG: 'avg',
  EXACT_MATCH: 'exact match',
  NOT_A_MATCH: 'not a match',
  CONTAINS_PII: 'encrypted columns only support exact matching',
} as const;
type MatchScoreLabel = typeof MatchScoreLabels[keyof typeof MatchScoreLabels];

export function getMatchScoreLabel(
  args: {
    components: AttributeComponent[];
    containsPii?: boolean;
  },
): MatchScoreLabel {
  const { components, containsPii } = args;

  if (containsPii) {
    return MatchScoreLabels.CONTAINS_PII;
  }

  const scoreComponent = components.find(
    component => component.attributeComponent.placeholder === CUSTOM_LIST_SCORE_PLACEHOLDER,
  );
  switch (scoreComponent?.value) {
    case '1':
      return MatchScoreLabels.EXACT_MATCH;
    case '0':
      return MatchScoreLabels.NOT_A_MATCH;
    default:
      return MatchScoreLabels.AVG;
  }
}

export function getCustomListDescription(
  args: {
    description: string;
    components: AttributeComponent[];
    containsPii?: boolean;
  },
): string {
  const { description, components, containsPii } = args;

  const isMultipleSelected = isMultipleOrAllColumnsSelected(components);
  const matchScoreLabel = getMatchScoreLabel({ components, containsPii });

  if (
    (isMultipleSelected && matchScoreLabel === MatchScoreLabels.AVG)
    || matchScoreLabel === MatchScoreLabels.EXACT_MATCH
    || matchScoreLabel === MatchScoreLabels.NOT_A_MATCH
    || matchScoreLabel === MatchScoreLabels.CONTAINS_PII
  ) {
    return `${description} (${matchScoreLabel})`;
  }

  return description;
}

export function getCustomListSourceColumns(
  args: {
    customLists: CustomList[];
    customListToken: string;
    majorVersion: number;
  },
): CustomListColumn[] {
  const { customLists, customListToken, majorVersion } = args;

  const customList = customLists.find(list => list.custom_list_token === customListToken);
  if (!customList) return [];

  // Each node can use a specific major version of one custom list,
  // every major version has its own set of possible values for the columns
  const currentVersion = customList.versions.find(
    version => version.major_version === majorVersion,
  );

  // Versions array is populated if the custom list was already used in a workflow
  // So on a fresh new workflow state, the versions array will be empty
  // We need to use the `active_columns` to get the possible values for the columns
  return !currentVersion ? customList.active_columns : currentVersion.columns;
}

export function checkIfSelectedContainsPII(
  selectedColumns: string[],
  sourceColumns: CustomListColumn[],
): boolean {
  if (selectedColumns.includes('all')) {
    return sourceColumns.some(column => column.contains_pii);
  }

  return sourceColumns.some(
    column => selectedColumns.includes(column.name) && column.contains_pii,
  );
}

export function getPossibleValuesForColumns(
  columns: CustomListColumn[],
): string[][] {
  const visibleColumns = filterOutMetadataColumns(columns);
  return [
    ['all', 'All'],
    ...visibleColumns.map(column => [column.name, column.name]),
  ];
}

export function getCustomListComponents(
  args: {
    components: AttributeComponent[];
    sourceColumns: CustomListColumn[];
  },
) {
  const { components, sourceColumns } = args;

  if (!sourceColumns?.length || !components?.length) {
    return { components, containsPii: false };
  }

  let containsPii = false;
  const updatedComponents = components.map(component => {
    // has precedence of 1
    if (component.attributeComponent.placeholder === CUSTOM_LIST_COLUMNS_PLACEHOLDER) {
      containsPii = checkIfSelectedContainsPII(
        component.value as string[],
        sourceColumns,
      );

      const possibleValues = getPossibleValuesForColumns(sourceColumns);

      return {
        ...component,
        attributeComponent: {
          ...component.attributeComponent,
          possibleValues,
        },
      };
    }

    // has precedence of 2
    if (component.attributeComponent.placeholder === CUSTOM_LIST_SCORE_PLACEHOLDER) {
      if (containsPii) {
        return {
          ...component,
          value: '1', // force exact match
          attributeComponent: {
            ...component.attributeComponent,
            possibleValues: '1,1', // enforce exact match
          },
        };
      }
    }
    return component;
  });

  return {
    components: updatedComponents,
    containsPii,
  };
}

// Updates a custom list attribute with updated components and description
// based on the custom list token and major version in action options
export function updateCustomListAttribute(
  args: {
    customLists: CustomList[];
    actionOptions: Record<string, ActionOption>;
    components: AttributeComponent[];
    description: string;
  },
) {
  const {
    components, customLists, actionOptions, description,
  } = args;
  if (!actionOptions) return { components, description };

  // Extract custom list token and major version from action options
  const customListToken = actionOptions.custom_list_token?.value?.toString();
  const majorVersion = actionOptions.custom_list_major_version?.value;

  if (!customListToken || !majorVersion) return { components, description };

  // Get source columns for the custom list based on token and version
  const sourceColumns = getCustomListSourceColumns({
    customLists,
    customListToken,
    majorVersion: Number(majorVersion),
  });

  const { components: updatedComponents, containsPii } = getCustomListComponents({
    components, sourceColumns,
  });

  const updatedDescription = getCustomListDescription({
    description,
    components: updatedComponents,
    containsPii,
  });

  return {
    components: updatedComponents,
    description: updatedDescription,
  };
}
